import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";

const BOOK_HREF = "https://cal.com/team/opensourcelinic/lyumjev-prescription";

const SOURCE_LINKS = {
  camaps: "https://doi.org/10.1089/dia.2023.0262",
  pronto: "https://doi.org/10.1089/dia.2020.0129",
  minimed: "https://doi.org/10.1177/15209156261444994",
} as const;

const LyumjevPage: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  usePageView();

  const steps = [
    {
      n: "1",
      title: t("LyumjevStep1Title"),
      body: t("LyumjevStep1Body"),
    },
    {
      n: "2",
      title: t("LyumjevStep2Title"),
      body: t("LyumjevStep2Body"),
    },
    {
      n: "3",
      title: t("LyumjevStep3Title"),
      body: t("LyumjevStep3Body"),
    },
  ];

  const stats = [
    {
      value: t("LyumjevStatCamapsValue"),
      label: t("LyumjevStatCamapsLabel"),
      detail: t("LyumjevStatCamapsDetail"),
    },
    {
      value: t("LyumjevStatProntoValue"),
      label: t("LyumjevStatProntoLabel"),
      detail: t("LyumjevStatProntoDetail"),
    },
    {
      value: t("LyumjevStatAhclValue"),
      label: t("LyumjevStatAhclLabel"),
      detail: t("LyumjevStatAhclDetail"),
    },
  ];

  const sources = [
    { text: t("LyumjevSourceCamaps"), href: SOURCE_LINKS.camaps },
    { text: t("LyumjevSourcePronto"), href: SOURCE_LINKS.pronto },
    { text: t("LyumjevSourceMinimed"), href: SOURCE_LINKS.minimed },
  ];

  return (
    <>
      <Seo
        title={t("LyumjevSeoTitle")}
        description={t("LyumjevSeoDescription")}
        canonical="https://www.opensource.clinic/lyumjev"
      />

      <Box sx={{ backgroundColor: "background.paper", minHeight: "100vh" }}>
        {/* Hero */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pt: { xs: 10, md: 14 },
            pb: { xs: 6, md: 8 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 680 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "primary.main",
                  mb: 1.5,
                  display: "block",
                }}
              >
                {t("LyumjevEyebrow")}
              </Typography>
              <Typography
                component="h1"
                variant="h1"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                  fontSize: smDown ? "2.75rem" : "4rem",
                }}
              >
                {t("LyumjevHeadline")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  maxWidth: 600,
                  fontSize: smDown ? "1rem" : "1.125rem",
                  lineHeight: 1.6,
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                {t("LyumjevSubheadline")}
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 4 }}
              >
                <Button
                  component="a"
                  href={BOOK_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: 999, px: 3, py: 1.25 }}
                >
                  {t("LyumjevCtaBook")}
                </Button>
                <Button
                  component="a"
                  href="#evidence"
                  variant="outlined"
                  size="large"
                  sx={{ borderRadius: 999, px: 3, py: 1.25 }}
                >
                  {t("LyumjevCtaEvidence")}
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* Steps */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Typography
            component="h2"
            variant="h3"
            sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}
          >
            {t("LyumjevStepsTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 640,
              mb: { xs: 4, md: 5 },
            }}
          >
            {t("LyumjevStepsSubtitle")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: { xs: 3, md: 4 },
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            }}
          >
            {steps.map((step) => (
              <Box key={step.n}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: "2rem",
                    fontWeight: 700,
                    color: "primary.main",
                    lineHeight: 1,
                    mb: 1.5,
                  }}
                >
                  {step.n}
                </Typography>
                <Typography
                  component="h3"
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.7 }}
                >
                  {step.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>

        {/* Evidence */}
        <Box
          id="evidence"
          sx={{
            borderTop: 1,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(99, 102, 241, 0.08)"
                : "#F7F9FC",
            py: { xs: 6, md: 8 },
            scrollMarginTop: 96,
          }}
        >
          <Container maxWidth="lg">
            <Typography
              component="h2"
              variant="h3"
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}
            >
              {t("LyumjevEvidenceTitle")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: 700,
                mb: { xs: 4, md: 5 },
              }}
            >
              {t("LyumjevEvidenceSubtitle")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: 3,
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              }}
            >
              {stats.map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    borderLeft: 3,
                    borderColor: "primary.main",
                    pl: 2.5,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: smDown ? "1.75rem" : "2rem",
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      lineHeight: 1.15,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 0.5, fontWeight: 700, color: "primary.main" }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary", lineHeight: 1.65 }}
                  >
                    {stat.detail}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: 5 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 1.5, color: "text.secondary" }}
              >
                {t("LyumjevSourcesTitle")}
              </Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 2.5, m: 0 }}>
                {sources.map((source) => (
                  <Typography
                    key={source.href}
                    component="li"
                    variant="body2"
                    sx={{ color: "text.secondary", lineHeight: 1.6 }}
                  >
                    <Link
                      href={source.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {source.text}
                    </Link>
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* Why we recommend + why via OSC */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              display: "grid",
              gap: { xs: 4, md: 6 },
              gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
              alignItems: "start",
            }}
          >
            <Box>
              <Typography
                component="h2"
                variant="h3"
                sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1.5 }}
              >
                {t("LyumjevWhyTitle")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", lineHeight: 1.7, mb: 2.5 }}
              >
                {t("LyumjevWhyBody")}
              </Typography>
            </Box>

            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                p: { xs: 3, sm: 3.5 },
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              <Typography
                component="h2"
                variant="h5"
                sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}
              >
                {t("LyumjevCtaBandTitle")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", lineHeight: 1.65, mb: 3 }}
              >
                {t("LyumjevCtaBandBody")}
              </Typography>
              <Button
                component="a"
                href={BOOK_HREF}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                fullWidth
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ borderRadius: 999, py: 1.35 }}
              >
                {t("LyumjevCtaBook")}
              </Button>
            </Box>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: { xs: 5, md: 6 },
              maxWidth: 760,
              color: "text.secondary",
              lineHeight: 1.6,
            }}
          >
            {t("LyumjevDisclaimer")}
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default LyumjevPage;
